import { supabase } from '../../lib/supabase';
import { format, parseISO, isValid, addDays } from 'date-fns';
import {
  UndertimeType,
  UNDERTIME_TYPE_LABELS,
  formatHours,
} from '../../utils/undertimeHours';

// Approvers for undertime — matches AdminRoute and the undertime RLS policies.
const APPROVER_ROLES = ['admin', 'superadmin'];

// Notifications are dropped after this long so the bell doesn't accumulate
// stale approval prompts, matching how other queue notifications behave.
const NOTIFICATION_TTL_DAYS = 30;

export interface UndertimeNotificationInput {
  undertimeId: string;
  employeeName: string;
  undertimeDate: string; // 'yyyy-MM-dd'
  hours: number;
  undertimeType: UndertimeType | string;
  /** Excluded from recipients — no point notifying the admin who filed it. */
  submittedByUserId?: string;
}

/**
 * Notify every admin / superadmin that an undertime request needs approval.
 *
 * Deliberately never throws: a notification failure must not lose an undertime
 * submission that already committed. Callers log and carry on.
 */
export async function notifyAdminsOfUndertime(
  input: UndertimeNotificationInput
): Promise<void> {
  try {
    const { data: approvers, error: approverError } = await supabase
      .from('profiles')
      .select('id')
      .in('role', APPROVER_ROLES)
      .is('deleted_at', null);

    if (approverError) throw approverError;

    const recipients = (approvers || [])
      .map((a: { id: string }) => a.id)
      .filter((id) => id && id !== input.submittedByUserId);

    if (recipients.length === 0) return;

    const parsedDate = parseISO(input.undertimeDate);
    const dateLabel = isValid(parsedDate) ? format(parsedDate, 'MMM dd') : input.undertimeDate;
    const typeLabel =
      UNDERTIME_TYPE_LABELS[input.undertimeType as UndertimeType] || input.undertimeType;

    const message = `${input.employeeName} filed ${formatHours(input.hours)} undertime on ${dateLabel} (${typeLabel}) — pending your approval`;

    const now = new Date().toISOString();
    const rows = recipients.map((recipientId) => ({
      recipient_id: recipientId,
      message,
      category: 'undertime',
      notification_type: 'undertime_submitted',
      is_read: false,
      is_active: true,
      reference_id: input.undertimeId,
      expiry_date: format(addDays(new Date(), NOTIFICATION_TTL_DAYS), 'yyyy-MM-dd'),
      created_at: now,
      updated_at: now,
    }));

    const { error: insertError } = await supabase.from('notifications').insert(rows);
    if (insertError) throw insertError;
  } catch (error) {
    console.error('Error creating undertime notifications:', error);
  }
}
